import { useState, useEffect } from 'react';
import {
  Layout,
  Typography,
  Button,
  Progress,
  Tag,
  Skeleton,
} from 'antd';
import {
  ArrowLeftOutlined,
  CheckCircleFilled,
} from '@ant-design/icons';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { DynamicForm, useStepNavigation } from '../dynamicForm';
import { axiosInstance, API_ENDPOINTS } from '../api';
import { creditsService } from '../api/services/creditsService';
import { dealerService } from '../api/services/dealerService';
import { useAuth } from '../hooks/useAuth';
import { useUserListingSteps } from './config/formSteps';
import { useStepHandlers } from './config/stepHandlers';
import NoCreditsModal from '../components/NoCreditsModal';
import './AddUserListingSimple.css';

const { Content } = Layout;
const { Title, Text } = Typography;

export default function AddUserListingSimple() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [mode] = useState<'edit' | 'view'>('edit');
  const [initialData, setInitialData] = useState<Record<string, any>>({});
  const [hydrated, setHydrated] = useState(false);
  const [showNoCreditsModal, setShowNoCreditsModal] = useState(false);
  const [checkingCredits, setCheckingCredits] = useState(false);

  const userListingSteps = useUserListingSteps();
  const handlers = useStepHandlers();
  const { currentStep, handleAction } = useStepNavigation({
    totalSteps: userListingSteps.length,
    handlers,
  });

  const handleFinalSubmit = (data: Record<string, any>) => {
    console.log('Final user listing submission:', data);
  };

  const progressPercentage = Math.round(
    ((currentStep + 1) / userListingSteps.length) * 100
  );

  const isEditMode = !!new URLSearchParams(window.location.search).get('id');

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const id = params.get('id');

    if (!id) {
      // Guard: dealers need an active subscription; regular users need credits
      setCheckingCredits(true);
      const isDealer = user?.role === 'DEALER';
      const dealerId = user?.dealerId;

      if (isDealer && dealerId) {
        dealerService.getActiveSubscription(dealerId)
          .then(sub => {
            if (sub && sub.status === 'ACTIVE') {
              setHydrated(true);
            } else {
              navigate('/dashboard/subscription');
            }
          })
          .catch(() => setHydrated(true)) // fail open
          .finally(() => setCheckingCredits(false));
      } else {
        creditsService.getBalance()
          .then(info => {
            const canPost = info.balance > 0 || info.isFree;
            if (!canPost) {
              setShowNoCreditsModal(true);
            } else {
              setHydrated(true);
            }
          })
          .catch(() => setHydrated(true)) // fail open — don't block on API error
          .finally(() => setCheckingCredits(false));
      }
      return;
    }

    (async () => {
      try {
        const { data } = await axiosInstance.get(
          API_ENDPOINTS.LISTINGS.GET_BY_ID(id)
        );
        const listing = data?.data || data?.listing || data;

        const extras = listing.extras ?? {};
        const merged: Record<string, any> = {
          // Step 1 — Vehicle Information (mobile parity)
          title: listing.title ?? '',
          year: listing.year ? String(listing.year) : '',
          make: listing.make ?? '',
          model: listing.model ?? '',
          price: listing.price ?? '',
          isNegotiable: listing.isNegotiable ?? true,
          mileage: listing.mileage ?? '',
          color: listing.color ?? '',
          fuelType: listing.fuelType ?? '',
          transmission: listing.transmission ?? '',
          bodyType: listing.bodyType ?? '',
          condition: listing.condition ?? 'USED',

          // Import fields
          isImported: listing.isImported ?? false,
          importCountry: listing.importCountry ?? '',
          shippingIncluded: listing.shippingIncluded ?? true,
          importAvailability: listing.importAvailability ?? '',
          estimatedArrivalDate: listing.estimatedArrivalDate ?? '',

          // Step 2 — Equipment & Features
          description: listing.description ?? '',
          doors: listing.doors ?? '',
          seats: listing.seats ?? '',
          features: Array.isArray(listing.features) ? listing.features : [],

          // Step 3 — Photos (handled by ImageUpload field reading existing URLs)
          images: Array.isArray(listing.images) ? listing.images : [],

          // Step 4 — Location
          county: extras.county ?? listing.location ?? '',
          city: listing.city ?? '',
          address: extras.address ?? '',

          // Step 5 — Contact
          telephoneNumber: listing.contact?.telephoneNumber ?? listing.user?.phone ?? '',
          displayPhoneNumber:
            listing.contact?.displayPhoneNumber === false ? 'no' : 'yes',
          showWhatsApp: listing.contact?.showWhatsApp ? 'yes' : 'no',

          // Internal — used by handlers to skip publish for already-live listings
          __status: listing.status ?? '',
        };

        setInitialData(merged);
      } catch (err) {
        console.warn('Failed to load listing for edit', err);
      } finally {
        setHydrated(true);
      }
    })();
  }, []);

  if (checkingCredits) {
    return (
      <Layout className="aul-layout">
        <Content className="aul-content">
          <div className="aul-skeleton-wrap">
            <Skeleton active paragraph={{ rows: 10 }} />
          </div>
        </Content>
      </Layout>
    );
  }

  if (showNoCreditsModal) {
    return (
      <Layout className="aul-layout">
        <Content className="aul-content">
          <NoCreditsModal onClose={() => navigate(-1)} />
        </Content>
      </Layout>
    );
  }

  if (!hydrated) {
    return (
      <Layout className="aul-layout">
        <Content className="aul-content">
          <div className="aul-skeleton-wrap">
            <Skeleton active paragraph={{ rows: 10 }} />
          </div>
        </Content>
      </Layout>
    );
  }

  return (
    <Layout className="aul-layout">
      <Content className="aul-content">
        <div className="aul-container">

          {/* ── Page Header ── */}
          <div className="aul-page-header">
            <Link to="/dashboard/ads">
              <Button
                className="aul-back-btn"
                icon={<ArrowLeftOutlined />}
                size="large"
              >
                {t('add_listing.my_listings')}
              </Button>
            </Link>
            <div className="aul-header-titles">
              <Title level={2} className="aul-page-title">
                {isEditMode ? t('add_listing.edit_listing') : t('add_listing.create_listing')}
              </Title>
              <Text className="aul-page-subtitle">
                {userListingSteps[currentStep]?.description}
              </Text>
            </div>
            <Tag className="aul-step-badge">
              {t('add_listing.step_badge', { current: currentStep + 1, total: userListingSteps.length })}
            </Tag>
          </div>

          {/* ── Mobile Progress (visible on ≤768px) ── */}
          <div className="aul-mobile-progress">
            <div className="aul-mobile-steps">
              {userListingSteps.map((step, idx) => (
                <div
                  key={idx}
                  className={[
                    'aul-mobile-step',
                    idx < currentStep ? 'completed' : '',
                    idx === currentStep ? 'active' : '',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                >
                  <div className="aul-mobile-step-dot">
                    {idx < currentStep ? <CheckCircleFilled /> : idx + 1}
                  </div>
                  <span className="aul-mobile-step-label">{step.title}</span>
                </div>
              ))}
            </div>
            <Progress
              percent={progressPercentage}
              strokeColor="#FE5A1D"
              trailColor="#f0f0f0"
              showInfo={false}
              strokeWidth={6}
              className="aul-progress-bar"
            />
          </div>

          {/* ── Two-column grid ── */}
          <div className="aul-grid">

            {/* ── Sidebar (desktop only) ── */}
            <aside className="aul-sidebar">
              <div className="aul-sidebar-inner">

                {/* Progress card */}
                <div className="aul-progress-card">
                  <div className="aul-progress-header">
                    <Text strong className="aul-progress-title">
                      {t('add_listing.your_progress')}
                    </Text>
                    <span className="aul-progress-pct">{progressPercentage}%</span>
                  </div>
                  <Progress
                    percent={progressPercentage}
                    strokeColor="#FE5A1D"
                    trailColor="#f0f0f0"
                    showInfo={false}
                    strokeWidth={8}
                  />
                </div>

                {/* Step list */}
                <div className="aul-step-list">
                  {userListingSteps.map((step, idx) => (
                    <div
                      key={idx}
                      className={[
                        'aul-step-item',
                        idx < currentStep ? 'completed' : '',
                        idx === currentStep ? 'active' : '',
                        idx > currentStep ? 'upcoming' : '',
                      ]
                        .filter(Boolean)
                        .join(' ')}
                    >
                      <div className="aul-step-icon-wrap">
                        {idx < currentStep ? (
                          <CheckCircleFilled className="aul-step-check" />
                        ) : (
                          <span className="aul-step-num">{idx + 1}</span>
                        )}
                      </div>
                      <div className="aul-step-info">
                        <Text strong className="aul-step-title">
                          {step.title}
                        </Text>
                        <Text className="aul-step-desc">
                          {step.description}
                        </Text>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Tips card */}
                <div className="aul-tips-card">
                  <Text strong className="aul-tips-title">
                    {t('add_listing.tips_title')}
                  </Text>
                  <ul className="aul-tips-list">
                    <li>{t('add_listing.tip_photos')}</li>
                    <li>{t('add_listing.tip_description')}</li>
                    <li>{t('add_listing.tip_price')}</li>
                    <li>{t('add_listing.tip_history')}</li>
                  </ul>
                </div>

              </div>
            </aside>

            {/* ── Form Area ── */}
            <main className="aul-main">
              <div className="aul-form-card">

                {/* Form card header */}
                <div className="aul-form-header">
                  <span className="aul-form-step-badge">
                    Step {currentStep + 1}
                  </span>
                  <Title level={4} className="aul-form-title">
                    {userListingSteps[currentStep]?.title}
                  </Title>
                  <Text className="aul-form-subtitle">
                    {userListingSteps[currentStep]?.description}
                  </Text>
                </div>

                {/* DynamicForm */}
                <div className="aul-form-body">
                  <DynamicForm
                    steps={userListingSteps}
                    currentStep={currentStep}
                    onAction={handleAction}
                    onSubmit={handleFinalSubmit}
                    mode={mode}
                    initialData={initialData}
                  />
                </div>

              </div>
            </main>

          </div>
        </div>
      </Content>
    </Layout>
  );
}
